import * as config from '@/config/env.config';
import { getAuth0Token, getTokenFromLocalStorage } from '@/utils/auth0Token';
import { getIsLoggingOut } from '@/utils/logoutState';

/**
 * Checks if the user is authenticated according to Auth0 SDK
 * This checks the Auth0 cache in localStorage to determine authentication state
 * @returns {boolean} True if user appears to be authenticated, false otherwise
 */
const isUserAuthenticated = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	// Only treat a user as authenticated when a non-expired token exists.
	return !!getTokenFromLocalStorage();
};

/**
 * Centralized 401/403 error handler
 * Redirects to /login for expired sessions or /securitycheck for authorization errors
 * 
 * This is used by:
 * - RTK Query APIs (baseQueryWithAuth)
 * - Legacy middleware functions (get, post, put, callApi, etc.)
 * 
 * Strategy:
 * - For 401 errors: Attempt token refresh once; if no valid token is returned, redirect to /login.
 * - For 403 errors: Redirect to /securitycheck (authorization issue) only when user appears authenticated.
 * 
 * @param {number|string} status - HTTP status code
 * @param {Object} errorData - Optional error data for logging
 * @param {string} endpoint - Optional endpoint for logging
 */
export const handleAuthError = async (status, errorData = null, endpoint = null) => {
	// Only handle 401 (Unauthorized) and 403 (Forbidden)
	if (status !== 401 && status !== 403) {
		return;
	}

	// Don't redirect if already on securitycheck, login, or landing page
	if (typeof window === 'undefined') {
		return;
	}

	const currentPath = window.location.pathname;
	const isLoggingOut = getIsLoggingOut();
	if (isLoggingOut) {
		return;
	}

	if (currentPath === '/securitycheck' || 
	    currentPath === '/login' || 
	    currentPath === '/') {
		return;
	}

	// For 401 errors, treat missing/expired/refresh-failed token as an auth-session issue.
	// If we cannot produce a valid token, redirect to login (never securitycheck for 401).
	if (status === 401) {
		try {
			const token = await getAuth0Token();
			if (token) {
				return;
			}
		} catch (error) {
			// Proceed to login redirect when token refresh check fails.
		}

		window.location.href = '/login';
		return;
	}

	// For 403, check if user is still authenticated according to Auth0 cache.
	// If not authenticated, the 403 is expected and we avoid redirect churn.
	const isAuthenticated = isUserAuthenticated();
	if (!isAuthenticated) {
		return;
	}

	const errorInfo = {
		status,
		message: errorData?.message || errorData?.error || 'Unauthorized/Forbidden',
		endpoint: endpoint || 'unknown',
		url: window.location.href,
		pathname: currentPath,
		userAuthenticated: true
	};

	if (config.ENABLE_SECURITY_ALERT) {
		alert('About to redirect to /securitycheck\n\nState:\n- Status: ' + status + '\n- Error: ' + JSON.stringify(errorInfo, null, 2) + '\n\nCheck console and state before clicking OK');
	}
	
	// Redirect to security check page
	window.location.href = '/securitycheck';
};

/**
 * Checks a fetch Response for 401/403 errors and handles them
 * @param {Response} response - Fetch Response object
 * @param {string} endpoint - Optional endpoint for logging
 * @returns {boolean} - True if error was handled (redirect initiated)
 */
export const handleResponseAuthError = async (response, endpoint = null) => {
	if (!response || !response.ok) {
		const status = response?.status;
		if (status === 401 || status === 403) {
			let errorData = null;
			try {
				errorData = await response.json().catch(() => ({}));
			} catch (e) {
				// If JSON parsing fails, use status text
				errorData = { error: response.statusText || 'Unknown error' };
			}
			await handleAuthError(status, errorData, endpoint);
			return true; // Error was handled
		}
	}
	return false; // No error or not a 401/403
};
